import { apiService } from './api';
import { NewsletterStats, NewsletterPostsStats, NewsletterBenchmarks, NewsletterInsights, NewsletterPostClicks, NewsletterHistoryStats } from '@/types/newsletter';

export class NewsletterService {
  async getCustomerPublicationStats(
    customerId: string,
    period?: string,
    customStartDate?: string,
    customEndDate?: string
  ): Promise<NewsletterStats> {
    try {
      // Build query parameters - customerId is required as a query param
      const params: any = {
        customerId: customerId
      };

      if (period) {
        params.period = period;
        if (period === 'custom' && customStartDate && customEndDate) {
          params.customStartDate = customStartDate;
          params.customEndDate = customEndDate;
        }
      }

      const response = await apiService.get<any>(
        `/publications/customer/${customerId}/stats`,
        params
      );
      
      // Extract the actual stats from the nested structure
      const rawData = response.data || response;
      const publication = rawData.publications && rawData.publications[0];
      const stats = publication?.latestStats;
      const performance = publication?.performance;

      if (!stats) {
        throw new Error('No publication stats found in response');
      }

      // Map the API fields to our expected structure
      // Use performance object for subscription metrics, latestStats for email metrics
      const mappedStats: NewsletterStats = {
        publication_id: publication.publicationId,
        publication_name: publication.publicationName,
        active_subscriptions: performance?.activeSubscriptions || 0,
        premium_subscriptions: performance?.activePremiumSubscriptions || 0,
        free_subscriptions: performance?.activeFreeSubscriptions || 0,
        average_open_rate: parseFloat(stats.averageOpenRate),
        average_click_rate: parseFloat(stats.averageClickRate),
        total_emails_sent: stats.totalSent,
        total_delivered: stats.totalDelivered,
        unique_opens: stats.totalUniqueOpened,
        total_clicks: stats.totalClicked,
        email_marketing_value: parseFloat(stats.emv.replace('$', '').replace(',', '')),
        referral_program_enabled: publication.referralProgramEnabled,
        snapshot_date: stats.snapshotDate
      };
      
      return mappedStats;
    } catch (error) {
      console.error('Newsletter API error:', error);
      throw error;
    }
  }

  async getCustomerPublications(customerId: string): Promise<any[]> {
    const response = await apiService.get<any>(`/publications/customer/${customerId}`);
    return response.data || response;
  }

  async getPublicationStatsHistory(publicationId: string, days: number = 90): Promise<NewsletterHistoryStats[]> {
    try {
      const response = await apiService.get<any>(`/publications/${publicationId}/stats/history`, { days });
      const rawData = response.data || response;

      // Map the API response to our history stats type
      const historyArray = rawData.statsHistory || [];
      const mappedHistory: NewsletterHistoryStats[] = historyArray.map((item: any) => ({
        snapshotDate: item.snapshotDate,
        activeSubscriptions: item.activeSubscriptions || 0,
        activeFreeSubscriptions: item.activeFreeSubscriptions || 0,
        activePremiumSubscriptions: item.activePremiumSubscriptions || 0,
        averageOpenRate: parseFloat(item.averageOpenRate || '0'),
        averageClickRate: parseFloat(item.averageClickRate || '0'),
        totalSent: item.totalSent || 0,
        totalDelivered: item.totalDelivered || 0,
        totalUniqueOpened: item.totalUniqueOpened || 0,
        totalClicked: item.totalClicked || 0,
        emv: parseFloat(item.publicationEmv || '0')
      }));

      return mappedHistory;
    } catch (error) {
      console.error('Newsletter history API error:', error);
      throw error;
    }
  }

  async getPublicationPostsStats(
    publicationId: string,
    period?: string,
    customStartDate?: string,
    customEndDate?: string
  ): Promise<NewsletterPostsStats> {
    try {
      // Build query parameters following the same pattern as customer stats
      const params: any = {};

      if (period) {
        params.period = period;
        if (period === 'custom' && customStartDate && customEndDate) {
          params.customStartDate = customStartDate;
          params.customEndDate = customEndDate;
        }
      }

      const response = await apiService.get<any>(
        `/publications/${publicationId}/posts/stats`,
        Object.keys(params).length > 0 ? params : undefined
      );
      
      const rawData = response.data || response;

      // Map only the basic post data without loading details
      const posts = rawData.posts?.map((post: any) => ({
        id: post.postId,
        title: post.title,
        publishDate: post.publishDate,
        publicationId: post.publicationId,
        webUrl: post.webUrl,
        stats: {
          totalSent: post.stats?.recipients || 0,
          totalDelivered: post.stats?.delivered || 0,
          totalUniqueOpened: post.stats?.uniqueOpens || 0,
          totalClicked: post.stats?.uniqueClicks || 0,
          averageOpenRate: parseFloat(post.stats?.openRate || '0'),
          averageClickRate: parseFloat(post.stats?.clickRate || '0'),
          emv: parseFloat(post.stats?.emv?.replace?.('$', '').replace?.(',', '') || '0'),
          snapshotDate: post.stats?.snapshotDate || ''
        },
        // Initialize with null - these will be loaded lazily when drawer opens
        benchmarks: null,
        insights: null,
        clicks: null
      })) || [];

      const mappedData: NewsletterPostsStats = {
        posts: posts,
        totalPosts: rawData.totalPosts || 0,
        dateRange: {
          start: rawData.dateRange?.start || '',
          end: rawData.dateRange?.end || ''
        }
      };
      
      return mappedData;
    } catch (error) {
      console.error('Newsletter posts stats API error:', error);
      throw error;
    }
  }

  async getPostBenchmarks(postId: string): Promise<NewsletterBenchmarks> {
    try {
      const response = await apiService.get<any>(`/publications/posts/${postId}/benchmarks`);
      
      const rawData = response.data || response;
      
      const mappedData: NewsletterBenchmarks = {
        post: {
          id: rawData.post?.id || '',
          title: rawData.post?.title || '',
          publishDate: rawData.post?.publishDate || '',
          publicationId: rawData.post?.publicationId || ''
        },
        publication: {
          id: rawData.publication?.id || '',
          name: rawData.publication?.name || '',
          organizationName: rawData.publication?.organizationName || '',
          customerId: rawData.publication?.customerId || ''
        },
        benchmarks: rawData.benchmarks || {},
        totalBenchmarks: rawData.totalBenchmarks || 0,
        availableMetrics: rawData.availableMetrics || []
      };
      
      return mappedData;
    } catch (error) {
      console.error('Newsletter benchmarks API error:', error);
      throw error;
    }
  }

  async getPostInsights(postId: string): Promise<NewsletterInsights> {
    try {
      const response = await apiService.get<any>(`/publications/posts/${postId}/insights`);
      
      const rawData = response.data || response;
      
      // Extract insights data - use the 'all' array which contains all insights
      // byCategory and byType contain the same insights grouped differently
      let insightsAll: any[] = [];
      if (rawData.insights?.all && Array.isArray(rawData.insights.all)) {
        insightsAll = rawData.insights.all;
      }

      // Calculate average confidence score from actual insights if not provided
      let avgConfidence = rawData.summary?.averageConfidenceScore;
      if ((avgConfidence == null || avgConfidence === 0) && insightsAll.length > 0) {
        const scores = insightsAll
          .map(i => {
            const score = i.confidenceScore;
            return typeof score === 'number' ? score : parseFloat(String(score));
          })
          .filter(s => !isNaN(s));

        if (scores.length > 0) {
          avgConfidence = scores.reduce((a, b) => a + b, 0) / scores.length;
        }
      }

      const mappedData: NewsletterInsights = {
        post: {
          id: rawData.post?.id || '',
          title: rawData.post?.title || '',
          publishDate: rawData.post?.publishDate || '',
          publicationId: rawData.post?.publicationId || ''
        },
        publication: {
          id: rawData.publication?.id || '',
          name: rawData.publication?.name || '',
          organizationName: rawData.publication?.organizationName || '',
          customerId: rawData.publication?.customerId || ''
        },
        insights: {
          all: insightsAll,
          byCategory: rawData.insights || {},
          byType: rawData.insights || {}
        },
        summary: {
          totalInsights: insightsAll.length,
          priorityBreakdown: rawData.summary?.priorityBreakdown || {},
          averageConfidenceScore: avgConfidence || null,
          availableCategories: rawData.summary?.availableCategories || [],
          availableTypes: rawData.summary?.availableTypes || []
        }
      };
      
      return mappedData;
    } catch (error) {
      console.error('Newsletter insights API error:', error);
      throw error;
    }
  }

  async getPostClicks(postId: string): Promise<NewsletterPostClicks> {
    try {
      const response = await apiService.get<any>(`/publications/posts/${postId}/clicks`);
      
      const rawData = response.data || response;
      
      const mappedData: NewsletterPostClicks = {
        post: {
          id: rawData.post?.id || '',
          title: rawData.post?.title || '',
          publishDate: rawData.post?.publishDate || '',
          publicationId: rawData.post?.publicationId || ''
        },
        publication: {
          id: rawData.publication?.id || '',
          name: rawData.publication?.name || '',
          organizationName: rawData.publication?.organizationName || '',
          customerId: rawData.publication?.customerId || ''
        },
        clickData: {
          mostRecentByUrl: rawData.clickData?.mostRecentByUrl || [],
          byDomain: rawData.clickData?.byDomain || {},
          byUtmSource: rawData.clickData?.byUtmSource || {}
        },
        summary: {
          totalUrls: rawData.summary?.totalUrls || 0,
          totalClicks: rawData.summary?.totalClicks || 0,
          totalUniqueClicks: rawData.summary?.totalUniqueClicks || 0,
          totalEmailClicks: rawData.summary?.totalEmailClicks || 0,
          totalWebClicks: rawData.summary?.totalWebClicks || 0,
          topDomains: rawData.summary?.topDomains || [],
          topUtmSources: rawData.summary?.topUtmSources || []
        }
      };
      
      return mappedData;
    } catch (error) {
      console.error('Newsletter clicks API error:', error);
      throw error;
    }
  }

  async getPostContentAnalysis(postId: string): Promise<any> {
    try {
      const response = await apiService.get<any>(`/publications/posts/${postId}/content-analysis`);
      return response;
    } catch (error) {
      console.error('Newsletter content analysis API error:', error);
      throw error;
    }
  }
}

export const newsletterService = new NewsletterService();